import React, { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import {
  FaPlus, FaLaptop, FaRegFileAlt, FaTags, FaUsers, FaUtensils,
  FaUniversity, FaGraduationCap, FaSyncAlt, FaCheckCircle, FaTimes,
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const API_INSTALLATION = 'https://dev.sliitleo.org/Backend/installation_reg.php';

/* ---------- helpers ---------- */

// Accept array, {all}, {rows}, {data}, {installations}. Fall back to [].
function pickInstallations(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload?.all && Array.isArray(payload.all)) return payload.all;
  if (payload?.rows && Array.isArray(payload.rows)) return payload.rows;
  if (payload?.data && Array.isArray(payload.data)) return payload.data;
  if (payload?.installations && Array.isArray(payload.installations)) return payload.installations;
  return [];
}

// Safe JSON parsing so HTML error pages don’t crash the app
async function toJsonSafe(res) {
  const text = await res.text();
  try { return JSON.parse(text); }
  catch {
    console.error('Invalid JSON from API:', text.slice(0, 800));
    throw new Error('Invalid JSON from server');
  }
}

// Normalize bank field across payloads
function getBank(item) {
  return (
    item.BankName ??
    item.Bank ??
    item.bank ??
    item.Bankname ??
    'Not Specified'
  ) || 'Not Specified';
}

// Normalize amount strings like "Rs. 1,500.00" to number
function parseAmount(v) {
  const n = parseFloat(String(v ?? '').replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

export default function Installation() {
  // user first – referenced in handlers
  const user = JSON.parse(localStorage.getItem('user')) || {
    SLIITID: '',
    name: 'Unknown User',
    role: 'Member',
    image: '/user.png'
  };

  // State
  const [verifyPopup, setVerifyPopup] = useState({ open: false, regid: null });
  const [installations, setInstallations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // Verify handlers (kept as-is)
  const handleVerify = (regid) => setVerifyPopup({ open: true, regid });
  const handleVerifyCancel = () => setVerifyPopup({ open: false, regid: null });

  const handleVerifyAccept = async () => {
    const regid = verifyPopup.regid;
    setVerifyPopup({ open: false, regid: null });
    try {
      const res = await fetch(API_INSTALLATION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `action=verify&RegID=${encodeURIComponent(regid)}&Verify=1&VerifiedBy=${encodeURIComponent(user.SLIITID)}`
      });
      const data = await toJsonSafe(res);
      if (data.success) {
        setInstallations(list =>
          list.map(item =>
            item.RegID === regid
              ? { ...item, Verify: 1, VerifiedBy: user.SLIITID, VerifiedDate: data.VerifiedDate }
              : item
          )
        );
      } else {
        alert('Failed to verify registration');
      }
    } catch {
      alert('Error verifying registration');
    }
  };

  // Check-in (kept as-is)
  const handleCheckIn = async (regid, currentStatus) => {
    if (currentStatus === 1 || currentStatus === '1' || currentStatus === true) return;
    try {
      const res = await fetch(API_INSTALLATION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `action=checkin&RegID=${encodeURIComponent(regid)}&CheckIn=1&CheckedBy=${encodeURIComponent(user.SLIITID)}`
      });
      const data = await toJsonSafe(res);
      if (data.success) {
        setInstallations(list =>
          list.map(item =>
            item.RegID === regid
              ? { ...item, CheckIn: 1, CheckedBy: user.SLIITID, CheckerName: user.name }
              : item
          )
        );
      } else {
        alert('Failed to update check-in status: ' + (data.message || 'Please try again'));
      }
    } catch {
      alert('An error occurred while processing your request');
    }
  };

  // Load
  const load = async () => {
    setErr('');
    setLoading(true);
    try {
      const res = await fetch(API_INSTALLATION, { method: 'GET' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const payload = await toJsonSafe(res);
      setInstallations(pickInstallations(payload));
    } catch (e) {
      console.error('Fetch error:', e);
      setInstallations([]);
      setErr('Could not load registrations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ---- Stats (robust) ----
  const totalMembers = installations.length;

  const totalCheckins = installations.reduce((acc, i) =>
    acc + ((i.CheckIn === 1 || i.CheckIn === '1' || i.CheckIn === true) ? 1 : 0)
  , 0);

  const myCheckins = installations.reduce((acc, i) =>
    acc + ((i.CheckedBy === user.SLIITID) && (i.CheckIn === 1 || i.CheckIn === '1' || i.CheckIn === true) ? 1 : 0)
  , 0);

  // Food stats
  const foodTally = installations.reduce((acc, item) => {
    const raw = String(item.FoodPref || '').trim().toLowerCase();
    if (/^veg(?:etarian)?$/.test(raw) || raw === 'vegan') acc.vegetarians++;
    else if (/^non[\s-]?veg(?:etarian)?$/.test(raw) || raw === 'non') acc.nonVegetarians++;
    else if (raw) acc.other++;
    else acc.unspecified++;
    return acc;
  }, { vegetarians: 0, nonVegetarians: 0, other: 0, unspecified: 0 });

  // Bank stats
  const bankBuckets = installations.reduce((acc, item) => {
    const bank = getBank(item);
    const amt = parseAmount(item.Amount);
    if (!acc[bank]) acc[bank] = { count: 0, totalAmount: 0 };
    acc[bank].count += 1;
    acc[bank].totalAmount += amt;
    return acc;
  }, {});
  const bankTransactions = Object.entries(bankBuckets).filter(([b]) => b !== 'Not Specified').reduce((n, [,v]) => n + v.count, 0);
  const totalAmount = Object.values(bankBuckets).reduce((sum, v) => sum + v.totalAmount, 0);
  const avgAmount = bankTransactions > 0 ? totalAmount / bankTransactions : 0;

  const estimatedTime = '0h 6m'; // your placeholder

  // UI
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 min-h-screen" style={{ color: 'var(--text-2)' }}>
        <span className="spinner lg" />
        <span style={{ fontSize: 13 }}>Loading installation registrations...</span>
      </div>
    );
  }

  return (
    <>
      {/* Verify popup */}
      {verifyPopup.open && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="verify-reg-title">
          <div className="modal-panel panel" style={{ maxWidth: 380 }}>
            <div className="modal-head">
              <h3 id="verify-reg-title">Verify Registration</h3>
              <button onClick={handleVerifyCancel} className="icon-btn" aria-label="Close">
                <FaTimes size={14} />
              </button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center' }}>
              <div
                style={{
                  width: 52, height: 52, borderRadius: 999, display: 'grid', placeItems: 'center',
                  background: 'rgba(63,174,106,.15)', color: 'var(--success)', margin: '0 auto 14px',
                }}
              >
                <FaCheckCircle size={24} />
              </div>
              <p style={{ fontSize: 13, color: 'var(--text-2)' }}>
                Are you sure you want to <span style={{ color: 'var(--success)', fontWeight: 600 }}>verify</span> this registration?
              </p>
            </div>
            <div className="modal-foot" style={{ justifyContent: 'center' }}>
              <button onClick={handleVerifyAccept} className="btn btn-primary">
                <FaCheckCircle size={13} /> Accept
              </button>
              <button onClick={handleVerifyCancel} className="btn btn-ghost">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex text-white min-h-screen" style={{ background: "var(--bg-base, #070a12)" }}>
        <Sidebar />
        <Topbar user={user} />
        <main className="lg:ml-64 mt-14 w-full">
          <div className="content">
            {/* Header */}
            <div className="page-head">
              <div>
                <div className="eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span
                    style={{
                      width: 26, height: 26, borderRadius: 8, flexShrink: 0,
                      background: 'var(--accent)', color: 'var(--accent-ink)',
                      display: 'grid', placeItems: 'center',
                    }}
                  >
                    <FaLaptop size={12} />
                  </span>
                  Installation / Registration
                </div>
                <h1>Registration Analytics</h1>
                <p className="page-desc">Comprehensive overview of registration data and statistics</p>
              </div>

              <div className="filter-row" style={{ flexWrap: 'wrap' }}>
                <Link to="/installation" className="btn btn-ghost">
                  <FaRegFileAlt size={13} /> Registration
                </Link>
                <Link to="/add-registration" className="btn btn-ghost">
                  <FaPlus size={13} /> Add Registration
                </Link>
                <Link to="/installation-report" className="btn btn-primary">
                  <FaTags size={13} /> Summary
                </Link>
              </div>
            </div>

            {/* Last updated / error */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
                Last updated: <span className="num">{new Date().toLocaleString()}</span>
              </span>
              {err && (
                <div className="error-banner" style={{ marginBottom: 0 }}>
                  <span>{err}</span>
                  <button onClick={load} className="btn btn-sm btn-ghost" style={{ marginLeft: 8, flexShrink: 0 }}>
                    <FaSyncAlt size={11} /> Reload
                  </button>
                </div>
              )}
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {/* Total Members */}
              <div className="panel tile">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="tile-label">Total Members</div>
                    <div className="tile-value num">{totalMembers.toLocaleString()}</div>
                  </div>
                  <div
                    style={{
                      width: 36, height: 36, borderRadius: 'var(--radius-sm)', flexShrink: 0,
                      background: 'rgba(47,111,237,.15)', color: 'var(--link)', display: 'grid', placeItems: 'center',
                    }}
                  >
                    <FaUsers size={15} />
                  </div>
                </div>
                <div className="flex items-center flex-wrap gap-2" style={{ fontSize: 11.5 }}>
                  <span className="pill pill-success">
                    <span className="dot" />
                    {totalCheckins} checked in
                  </span>
                  <span style={{ color: 'var(--text-3)' }}>
                    {Math.max(0, totalMembers - totalCheckins)} pending
                  </span>
                </div>
              </div>

              {/* Food Preferences */}
              <div className="panel tile">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="tile-label">Food Preferences</div>
                    <div className="tile-value num">
                      {(foodTally.vegetarians + foodTally.nonVegetarians).toLocaleString()}
                    </div>
                  </div>
                  <div
                    style={{
                      width: 36, height: 36, borderRadius: 'var(--radius-sm)', flexShrink: 0,
                      background: 'rgba(124,58,237,.15)', color: 'var(--accent)', display: 'grid', placeItems: 'center',
                    }}
                  >
                    <FaUtensils size={14} />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2" style={{ fontSize: 11.5 }}>
                  <span className="pill pill-success"><span className="dot" />{foodTally.vegetarians} Veg</span>
                  <span className="pill pill-danger"><span className="dot" />{foodTally.nonVegetarians} Non-Veg</span>
                  {foodTally.other > 0 && (
                    <span className="pill pill-warning"><span className="dot" />{foodTally.other} Other</span>
                  )}
                </div>
              </div>

              {/* Bank Transactions */}
              <div className="panel tile">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="tile-label">Bank Transactions</div>
                    <div className="tile-value num">{bankTransactions.toLocaleString()}</div>
                  </div>
                  <div
                    style={{
                      width: 36, height: 36, borderRadius: 'var(--radius-sm)', flexShrink: 0,
                      background: 'rgba(63,174,106,.15)', color: 'var(--success)', display: 'grid', placeItems: 'center',
                    }}
                  >
                    <FaUniversity size={14} />
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
                  <div className="flex items-center justify-between">
                    <span style={{ color: 'var(--text-3)' }}>Total:</span>
                    <span className="num" style={{ fontWeight: 600 }}>LKR {totalAmount.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span style={{ color: 'var(--text-3)' }}>Avg:</span>
                    <span className="num">LKR {avgAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Faculties */}
              <div className="panel tile">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="tile-label">Faculties</div>
                    <div className="tile-value num">
                      {Object.keys(
                        installations.reduce((acc, item) => {
                          const f = item.Faculty || 'Not Specified';
                          acc[f] = true; return acc;
                        }, {})
                      ).length.toLocaleString()}
                    </div>
                  </div>
                  <div
                    style={{
                      width: 36, height: 36, borderRadius: 'var(--radius-sm)', flexShrink: 0,
                      background: 'rgba(217,154,61,.15)', color: 'var(--warning)', display: 'grid', placeItems: 'center',
                    }}
                  >
                    <FaGraduationCap size={14} />
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
                  <div className="flex items-center justify-between">
                    <span style={{ color: 'var(--text-3)' }}>Members:</span>
                    <span className="num" style={{ fontWeight: 600 }}>{installations.length.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between" style={{ gap: 8 }}>
                    <span style={{ color: 'var(--text-3)', flexShrink: 0 }}>Largest:</span>
                    <span className="truncate" style={{ maxWidth: 150 }} title={(() => {
                      const facultyCounts = installations.reduce((acc, item) => {
                        const f = item.Faculty || 'Not Specified';
                        acc[f] = (acc[f] || 0) + 1; return acc;
                      }, {});
                      const largest = Object.entries(facultyCounts).sort((a,b)=>b[1]-a[1])[0];
                      return largest ? `${largest[0]} (${largest[1]})` : 'N/A';
                    })()}>
                      {(() => {
                        const facultyCounts = installations.reduce((acc, item) => {
                          const f = item.Faculty || 'Not Specified';
                          acc[f] = (acc[f] || 0) + 1; return acc;
                        }, {});
                        const largest = Object.entries(facultyCounts).sort((a,b)=>b[1]-a[1])[0];
                        return largest ? `${largest[0]} (${largest[1]})` : 'N/A';
                      })()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bank-wise Summary */}
            <div className="panel" style={{ marginBottom: 'var(--sp-6)' }}>
              <div className="section-head">
                <div>
                  <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FaUniversity size={14} style={{ color: 'var(--link)' }} />
                    Bank-wise Summary
                  </h2>
                  <p style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>
                    Financial breakdown by banking institution
                  </p>
                </div>
                <div className="chip">{Object.keys(bankBuckets).length} Banks</div>
              </div>

              {Object.keys(bankBuckets).length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon"><FaUniversity /></div>
                  <h3>No bank data yet</h3>
                  <p>Bank transactions will appear here once registrations include payment details.</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Bank Name</th>
                        <th style={{ textAlign: 'right' }}>Members</th>
                        <th style={{ textAlign: 'right' }}>Total Amount</th>
                        <th style={{ textAlign: 'right' }}>Avg. Per Member</th>
                        <th style={{ textAlign: 'right' }}>% of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(bankBuckets)
                        .sort((a, b) => b[1].count - a[1].count)
                        .map(([bank, data]) => {
                          const percentage = totalMembers ? (data.count / totalMembers) * 100 : 0;
                          return (
                            <tr key={bank}>
                              <td className="proj-name">{bank}</td>
                              <td className="num" style={{ textAlign: 'right' }}>
                                {data.count}
                                <span style={{ marginLeft: 4, fontSize: 11, color: 'var(--text-3)' }}>
                                  ({percentage.toFixed(1)}%)
                                </span>
                              </td>
                              <td className="num" style={{ textAlign: 'right', color: 'var(--link)', fontWeight: 600 }}>
                                Rs. {data.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="num" style={{ textAlign: 'right', color: 'var(--text-2)' }}>
                                Rs. {(data.count ? (data.totalAmount / data.count) : 0).toFixed(2)}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                <div className="flex items-center justify-end gap-2">
                                  <div style={{ width: 80, height: 6, background: 'var(--surface-3)', borderRadius: 999 }}>
                                    <div style={{ width: `${percentage}%`, height: '100%', background: 'var(--link)', borderRadius: 999 }} />
                                  </div>
                                  <span className="num" style={{ fontSize: 11, color: 'var(--text-3)' }}>{percentage.toFixed(1)}%</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Faculty and Food sections */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
              {/* Faculty Distribution */}
              <div className="panel" style={{ padding: 'var(--sp-5)' }}>
                <div style={{ marginBottom: 'var(--sp-5)' }}>
                  <h2 style={{ fontSize: 14.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FaGraduationCap size={14} style={{ color: 'var(--link)' }} />
                    Faculty Distribution
                  </h2>
                  <p style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>
                    Member distribution across faculties
                  </p>
                </div>

                {installations.length === 0 ? (
                  <div className="empty-state" style={{ padding: 'var(--sp-6) 0' }}>
                    <div className="empty-icon"><FaGraduationCap /></div>
                    <h3>No registrations yet</h3>
                    <p>Faculty distribution will show up once members register.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
                    {Object.entries(
                      installations.reduce((acc, item) => {
                        const faculty = item.Faculty || 'Not Specified';
                        acc[faculty] = (acc[faculty] || 0) + 1;
                        return acc;
                      }, {})
                    ).sort((a,b)=>b[1]-a[1]).map(([faculty, count]) => {
                      const pct = totalMembers ? (count / totalMembers) * 100 : 0;
                      return (
                        <div key={faculty}>
                          <div className="flex justify-between" style={{ fontSize: 12.5, marginBottom: 6 }}>
                            <span style={{ color: 'var(--text-2)' }}>{faculty}</span>
                            <span className="num" style={{ fontWeight: 600 }}>
                              {count} <span style={{ color: 'var(--text-3)', fontSize: 11 }}>({pct.toFixed(1)}%)</span>
                            </span>
                          </div>
                          <div style={{ width: '100%', background: 'var(--surface-3)', borderRadius: 999, height: 6 }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: 'var(--accent)', borderRadius: 999 }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Food Preferences */}
              <div className="panel" style={{ padding: 'var(--sp-5)' }}>
                <div style={{ marginBottom: 'var(--sp-5)' }}>
                  <h2 style={{ fontSize: 14.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FaUtensils size={14} style={{ color: 'var(--success)' }} />
                    Food Preferences
                  </h2>
                  <p style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>
                    Dietary preferences of registered members
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Veg */}
                  <div className="panel" style={{ padding: 'var(--sp-4)', background: 'var(--surface-2)' }}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--success)' }}>Vegetarian</div>
                        <div className="num" style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{foodTally.vegetarians}</div>
                        <div style={{ fontSize: 11, color: 'var(--success)', marginTop: 4 }}>
                          {totalMembers ? ((foodTally.vegetarians / totalMembers) * 100).toFixed(1) : '0.0'}% of total
                        </div>
                      </div>
                      <div style={{ width: 30, height: 30, borderRadius: 'var(--radius-sm)', display: 'grid', placeItems: 'center', background: 'rgba(63,174,106,.15)', color: 'var(--success)' }}>
                        <FaCheckCircle size={13} />
                      </div>
                    </div>
                    <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                      <div style={{ height: 5, width: '100%', background: 'var(--surface-3)', borderRadius: 999, overflow: 'hidden' }}>
                        <div style={{ height: '100%', background: 'var(--success)', width: `${totalMembers ? (foodTally.vegetarians / totalMembers) * 100 : 0}%` }} />
                      </div>
                    </div>
                  </div>

                  {/* Non-Veg */}
                  <div className="panel" style={{ padding: 'var(--sp-4)', background: 'var(--surface-2)' }}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--error)' }}>Non-Vegetarian</div>
                        <div className="num" style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{foodTally.nonVegetarians}</div>
                        <div style={{ fontSize: 11, color: 'var(--error)', marginTop: 4 }}>
                          {totalMembers ? ((foodTally.nonVegetarians / totalMembers) * 100).toFixed(1) : '0.0'}% of total
                        </div>
                      </div>
                      <div style={{ width: 30, height: 30, borderRadius: 'var(--radius-sm)', display: 'grid', placeItems: 'center', background: 'rgba(226,100,92,.15)', color: 'var(--error)' }}>
                        <FaUtensils size={13} />
                      </div>
                    </div>
                    <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                      <div style={{ height: 5, width: '100%', background: 'var(--surface-3)', borderRadius: 999, overflow: 'hidden' }}>
                        <div style={{ height: '100%', background: 'var(--error)', width: `${totalMembers ? (foodTally.nonVegetarians / totalMembers) * 100 : 0}%` }} />
                      </div>
                    </div>
                  </div>

                  {/* Other */}
                  {foodTally.other > 0 && (
                    <div className="panel" style={{ padding: 'var(--sp-4)', background: 'var(--surface-2)' }}>
                      <div className="flex justify-between items-start">
                        <div>
                          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--warning)' }}>Other</div>
                          <div className="num" style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{foodTally.other}</div>
                          <div style={{ fontSize: 11, color: 'var(--warning)', marginTop: 4 }}>
                            {totalMembers ? ((foodTally.other / totalMembers) * 100).toFixed(1) : '0.0'}% of total
                          </div>
                        </div>
                        <div style={{ width: 30, height: 30, borderRadius: 'var(--radius-sm)', display: 'grid', placeItems: 'center', background: 'rgba(217,154,61,.15)', color: 'var(--warning)' }}>
                          <FaUtensils size={13} />
                        </div>
                      </div>
                      <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                        <div style={{ height: 5, width: '100%', background: 'var(--surface-3)', borderRadius: 999, overflow: 'hidden' }}>
                          <div style={{ height: '100%', background: 'var(--warning)', width: `${totalMembers ? (foodTally.other / totalMembers) * 100 : 0}%` }} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Unspecified */}
                  {foodTally.unspecified > 0 && (
                    <div className="panel" style={{ padding: 'var(--sp-4)', background: 'var(--surface-2)' }}>
                      <div className="flex justify-between items-start">
                        <div>
                          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-2)' }}>Not Specified</div>
                          <div className="num" style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{foodTally.unspecified}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                            {totalMembers ? ((foodTally.unspecified / totalMembers) * 100).toFixed(1) : '0.0'}% of total
                          </div>
                        </div>
                        <div style={{ width: 30, height: 30, borderRadius: 'var(--radius-sm)', display: 'grid', placeItems: 'center', background: 'var(--surface-3)', color: 'var(--text-3)' }}>
                          <FaUtensils size={13} />
                        </div>
                      </div>
                      <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                        <div style={{ height: 5, width: '100%', background: 'var(--surface-3)', borderRadius: 999, overflow: 'hidden' }}>
                          <div style={{ height: '100%', background: 'var(--text-3)', width: `${totalMembers ? (foodTally.unspecified / totalMembers) * 100 : 0}%` }} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* (Optional) You can render a small list and wire verify/check-in here if needed */}
            {/* Example:
              installations.slice(0, 5).map(row => (
                <button onClick={() => handleVerify(row.RegID)}>Verify {row.Name}</button>
              ))
            */}
          </div>
        </main>
      </div>
    </>
  );
}
